import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

import { customerService } from '../../../services/api';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SoftChip from '../../../components/dashboard/SoftChip';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { formatDateTime } from '../../../utils/formatters';
import { CollaboratorStatusChip, capabilities, inviteErrorMessage } from './collaboratorStatus';
import { displayStatus } from './collaboratorFilters';

const LABEL_PROPS = { variant: 'body2', color: 'text.secondary' };
const VALUE_PROPS = { variant: 'body1', color: 'text.primary' };

function Field({ label, value, divider = true }) {
  return (
    <ListItem disableGutters divider={divider}>
      <ListItemText
        primary={label}
        secondary={value}
        primaryTypographyProps={LABEL_PROPS}
        secondaryTypographyProps={typeof value === 'string' ? VALUE_PROPS : { component: 'div' }}
      />
    </ListItem>
  );
}

const INVITE_HINT_KEY = {
  pending: 'collaborators.invite.pendingHint',
  invited: 'collaborators.invite.invitedHint',
  expired: 'collaborators.invite.expiredHint',
  active: 'collaborators.invite.activeHint',
};

export default function CollaboratorDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [flashMessage, setFlashMessage] = useState('');
  const [inviteError, setInviteError] = useState('');

  const collaboratorQuery = useQuery({
    queryKey: ['collaborator', id],
    retry: retryOnServerError,
    queryFn: () => customerService.getCollaborator(id).then((res) => res.data),
  });
  const collaborator = collaboratorQuery.data;

  const refresh = (data) => {
    queryClient.setQueryData(['collaborator', id], data);
    queryClient.invalidateQueries({ queryKey: ['collaborators'] });
  };

  const sendInvite = useMutation({
    mutationFn: () => customerService.sendCollaboratorInvite(id).then((res) => res.data),
    onSuccess: (data) => {
      refresh(data);
      setInviteError('');
      setFlashMessage(t('collaborators.invite.sent', { email: data.email }));
    },
    onError: (error) => setInviteError(inviteErrorMessage(t, error)),
  });

  const restore = useMutation({
    mutationFn: () => customerService.restoreCollaborator(id).then((res) => res.data),
    onSuccess: refresh,
  });

  const handleDelete = async () => {
    await customerService.deleteCollaborator(id);
    queryClient.invalidateQueries({ queryKey: ['collaborators'] });
    queryClient.removeQueries({ queryKey: ['collaborator', id] });
    navigate('/collaborators', {
      state: { seedDelete: { ids: [id], message: t('collaborators.deleteSuccess') } },
    });
  };

  const title = collaborator?.name ?? t('collaborators.detailTitle');
  const header = (
    <PageHeader
      title={title}
      breadcrumbs={[
        { label: t('collaborators.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('collaborators.breadcrumb.list'), to: '/collaborators' },
        { label: title },
      ]}
    />
  );

  const renderBody = () => {
    if (collaboratorQuery.isLoading) {
      return <LoadingState message={t('collaborators.states.loading')} />;
    }

    if (collaboratorQuery.isError) {
      const status = collaboratorQuery.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('collaborators.states.permissionDeniedTitle')}
            message={t('collaborators.states.permissionDeniedMessage')}
          />
        );
      }
      if (status === 404) {
        return (
          <ErrorState
            title={t('collaborators.states.notFoundTitle')}
            message={t('collaborators.states.notFoundMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('collaborators.states.errorTitle')}
          message={t('collaborators.states.errorMessage')}
          actionLabel={t('collaborators.states.retry')}
          onRetry={collaboratorQuery.refetch}
        />
      );
    }

    const can = capabilities(collaborator);
    const { invitation } = collaborator;
    const status = displayStatus(collaborator);
    const isAll = collaborator.business_scope === 'all';

    return (
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader
            title={t('collaborators.sections.identification')}
            subheader={t('collaborators.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <Field label={t('collaborators.fields.name')} value={collaborator.name} />
              <Field label={t('collaborators.fields.email')} value={collaborator.email} />
              <Field
                label={t('collaborators.fields.department')}
                value={collaborator.department?.name ?? t('collaborators.fields.noDepartment')}
              />
              <Field
                label={t('collaborators.fields.status')}
                divider={false}
                value={
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <CollaboratorStatusChip t={t} collaborator={collaborator} />
                    {collaborator.is_owner && <SoftChip size="small" color="primary" label={t('collaborators.owner')} />}
                  </Stack>
                }
              />
            </List>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            {can.can_delete && (
              <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete}>
                {t('collaborators.actions.delete')}
              </Button>
            )}
            {can.can_restore && (
              <Button
                variant="outlined"
                startIcon={<RestoreOutlinedIcon />}
                onClick={() => restore.mutate()}
                disabled={restore.isPending}
              >
                {t('collaborators.actions.restore')}
              </Button>
            )}
            {can.can_edit && (
              <Button
                variant="contained"
                startIcon={<EditOutlinedIcon />}
                onClick={() => navigate(`/collaborators/${id}/edit`)}
              >
                {t('collaborators.actions.edit')}
              </Button>
            )}
          </CardActions>
        </Card>

        <Card>
          <CardHeader
            title={t('collaborators.sections.access')}
            subheader={isAll ? t('collaborators.scopeHelpers.all') : t('collaborators.sections.accessHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              {isAll ? (
                <Field
                  label={t('collaborators.scopes.all')}
                  value={collaborator.global_role?.name ?? ''}
                  divider={false}
                />
              ) : (
                collaborator.memberships.map((membership, index) => (
                  <Field
                    key={membership.uuid}
                    label={membership.business.name}
                    divider={index < collaborator.memberships.length - 1}
                    value={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <span>{membership.role.name}</span>
                        {membership.is_primary && (
                          <SoftChip size="small" color="info" label={t('collaborators.fields.primary')} />
                        )}
                      </Stack>
                    }
                  />
                ))
              )}
            </List>
          </CardContent>
        </Card>

        {collaborator.status !== 'removed' && (
          <Card>
            <CardHeader
              title={t('collaborators.sections.invitation')}
              subheader={INVITE_HINT_KEY[status] ? t(INVITE_HINT_KEY[status]) : null}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <Divider />
            <CardContent sx={{ py: 0 }}>
              <List disablePadding>
                <Field
                  label={t('collaborators.fields.invitedAt')}
                  value={
                    invitation?.invited_at
                      ? formatDateTime(invitation.invited_at, i18n.language)
                      : t('collaborators.invite.notSent')
                  }
                />
                {collaborator.status === 'invited' && invitation?.expires_at && (
                  <Field
                    label={t('collaborators.fields.linkExpiresAt')}
                    value={formatDateTime(invitation.expires_at, i18n.language)}
                  />
                )}
                <Field
                  label={t('collaborators.fields.acceptedAt')}
                  value={formatDateTime(invitation?.accepted_at, i18n.language)}
                  divider={false}
                />
              </List>
              {inviteError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {inviteError}
                </Alert>
              )}
            </CardContent>
            {can.can_send_invite && (
              <>
                <Divider />
                <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant={status === 'invited' ? 'outlined' : 'contained'}
                    startIcon={<SendOutlinedIcon />}
                    onClick={() => sendInvite.mutate()}
                    disabled={sendInvite.isPending}
                  >
                    {collaborator.status === 'invited' ? t('collaborators.invite.resend') : t('collaborators.invite.send')}
                  </Button>
                </CardActions>
              </>
            )}
          </Card>
        )}
      </Stack>
    );
  };

  return (
    <BoundedPageLayout header={header}>
      {renderBody()}
      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
